import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { MODULES, type ModuleId } from "@/modules/registry";
import { ModuleSetupChecklist } from "@/modules/apps/ModuleSetupChecklist";
import { useModuleSetup } from "@/modules/apps/useModuleSetup";

/** Centered guide: what's left to do before a module works. */
export const ModuleSetupDialog = ({
  moduleId,
  open,
  onOpenChange,
}: {
  moduleId: ModuleId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const module = MODULES[moduleId];
  const setup = useModuleSetup(moduleId, { enabled: open });
  const Icon = module.icon;
  const percent = setup.requiredTotal
    ? Math.round((setup.requiredDone / setup.requiredTotal) * 100)
    : 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="mb-1 flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="size-6" />
          </div>
          <DialogTitle>
            {setup.isReady
              ? `${module.name} is ready`
              : `Set up ${module.name}`}
          </DialogTitle>
          <DialogDescription>
            {setup.isReady
              ? "Everything it needs is connected. You can come back here from Apps."
              : "A few steps and it's ready for your team."}
          </DialogDescription>
        </DialogHeader>

        {setup.requiredTotal > 0 ? (
          <div className="flex items-center gap-3">
            <Progress value={percent} className="h-2" />
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {setup.requiredDone}/{setup.requiredTotal}
            </span>
          </div>
        ) : null}

        <ModuleSetupChecklist
          steps={setup.steps}
          isPending={setup.isPending}
          onNavigate={() => onOpenChange(false)}
        />

        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            {setup.isReady ? "Done" : "Later"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
